'use client';

import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { isNotEmpty, useForm } from '@mantine/form';
import FormControl from '../Form/FormControl';
import TextInput from '../Form/TextInput';
import Button from '../Form/Button';
import Form from '../Form/Form';
import useLoadTracker from '../../Hooks/useLoadTracker';

export type ForgotPasswordFormProps = {
	className?: string;
	onSubmit?: (values: ForgotPasswordFormType) => Promise<void>;
	children?: ReactNode;
};

export default function ForgotPasswordForm(props: ForgotPasswordFormProps) {
	const { className, onSubmit, children } = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const [error, setError] = useState<ReactNode | null>(null);
	const [hasSubmitted, setHasSubmitted] = useState(false);

	const form = useForm<ForgotPasswordFormType>({
		initialValues: {
			email: '',
		},
		validate: {
			email: isNotEmpty('Email is required'),
		},
	});

	const onFormSubmit = async (values: ForgotPasswordFormType) => {
		if (!onSubmit) {
			return;
		}

		const loader = addLoader();
		try {
			setError(null);
			await onSubmit(values);
			setHasSubmitted(true);
		} catch (err) {
			if (err instanceof Error) {
				setError(err.message);
			} else if (err) {
				setError(`${err}`);
			} else {
				setError('Unknown error');
			}
		} finally {
			removeLoader(loader);
		}
	};

	return (
		<div className={`ForgotPasswordForm ${className ?? ''}`}>
			<Form form={form} onSubmit={onFormSubmit} disabled={isLoading}>
				{hasSubmitted ? (
					<p>Please check your email address for further instructions.</p>
				) : (
					<>
						<FormControl name="email" label="Email">
							<TextInput type="email" placeholder="Your email address" />
						</FormControl>
						<Button type="submit" isLoading={isLoading}>
							Reset password
						</Button>
						{error && <div className="error">{error}</div>}
					</>
				)}
				{children}
			</Form>
		</div>
	);
}

export type ForgotPasswordFormType = {
	email: string;
};
